import React, { useState, useRef, useEffect } from 'react';
import { FiShoppingCart, FiHome } from 'react-icons/fi';
import { Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import eventBus from '../utils/eventBus';
import propertiesService from '../services/propertiesService';

const FloatingCart = ({ cartItems = {}, properties, setCartItems, refreshProperties, removeFromCart }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const hoverTimeoutRef = useRef(null);
  const [propertiesList, setPropertiesList] = useState(properties || []);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  useEffect(() => {
    // Fixed: Subscribe to properties updates
    const unsubscribeProperties = eventBus.on('properties:updated', (updatedProperties) => {
      if (updatedProperties && Array.isArray(updatedProperties)) {
        setPropertiesList(updatedProperties);
      }
    });
    
    // Fixed: Subscribe to property creation events
    const unsubscribePropertyCreated = eventBus.on('property:created', async (response) => {
      if (response && response.property_id) {
        // Fetch the newly created property details
        try {
          const propertyResponse = await propertiesService.getPropertyById(response.property_id);
          if (propertyResponse && propertyResponse.property) {
            // Add the new property to the properties list
            setPropertiesList(prev => {
              // Check if property already exists to avoid duplicates
              const exists = prev.some(p => p.id === propertyResponse.property.id);
              if (!exists) {
                return [...prev, propertyResponse.property];
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('Error fetching newly created property:', error);
        }
      }
    });
    
    // Cleanup
    return () => {
      unsubscribeProperties();
      unsubscribePropertyCreated();
    };
  }, []);

  // Fixed: Update properties list when the prop changes
  useEffect(() => {
    if (properties && Array.isArray(properties)) {
      setPropertiesList(properties);
    }
  }, [properties]);

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  // Check if we're on the cart page to hide the floating cart
  if (window.location?.pathname === '/cart') {
    return null;
  }

  // Don't render if no cart items
  if (!cartItems || Object.keys(cartItems).length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 right-4 z-50"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative">
        <button
          onClick={() => navigate('/cart')}
          style={{ border: '2px solid white', borderRadius: 50 }}
          className="bg-golden-navy text-white p-3 rounded shadow-lg hover:bg-opacity-90 transition-all duration-300 flex items-center"
        >
          <div className="relative">
            <FiShoppingCart className="h-6 w-6" />
            {Object.keys(cartItems).length > 0 && (
              <span className="absolute -top-6 -right-4 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {Object.keys(cartItems).length}
              </span>
            )}
          </div>
        </button>

        {isHovered && Object.keys(cartItems).length > 0 && (
          <div className="absolute bottom-full right-0 mb-0 w-96 bg-white rounded-lg shadow-xl border">
            <div className="divide-y max-h-96 overflow-y-auto min-h-[300px] max-h-[300px]">
              {Object.entries(cartItems).map(([propertyId, pp]) => {
                // Fixed: Enhanced property lookup with fallback fetching
                let property = propertiesList.find(i => i.id == propertyId);
                
                if (!property) {
                  // Show loading state while fetching
                  return (
                    <div key={propertyId} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <FiHome className="h-6 w-6 mx-1 text-gray-400" />
                        <div className="w-full">
                          <div className="font-medium">Property #{propertyId}</div>
                          <div className="text-sm text-gray-500">Details unavailable</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-sm text-gray-500">
                          {new Date().toLocaleDateString('en-GB', { 
                            weekday: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromCart(propertyId);
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={propertyId} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={property?.property_type_id === 1 ? '/ico/propertyHouseIco.svg' : '/ico/propertyBuildingIco.svg'} 
                        alt="Property Icon" 
                        className="h-6 w-6 mx-1"
                      />                    
                      <div className='w-full'>
                        <div className="font-medium">{property?.property_title}</div>
                        <div 
                          style={{ minHeight: '24px', fontSize: '10px' }} 
                          className="text-sm text-gray-500"
                        >
                          {property?.apartment_address} {property?.apartment_number}, {property?.delivery_zip_code} {property?.delivery_city}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm text-gray-500">
                        {new Date().toLocaleDateString('en-GB', { 
                          weekday: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromCart(propertyId);
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="p-1 bg-gray-50 rounded-b-lg">
              <button 
                onClick={() => navigate('/cart')}
                className="w-full text-white py-1 rounded bg-golden-navy transition-all duration-300"
              >
                GO TO CART
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingCart;
